import { useState } from 'react';
import { X, Loader2, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useFinancialSettings,
  useUpdateFinancialSettings,
  useFinancialSync,
} from '../../hooks/useFinancial';

interface StripeIntegrationFormProps {
  onClose: () => void;
}

export function StripeIntegrationForm({ onClose }: StripeIntegrationFormProps) {
  const { data: settingsData, isLoading } = useFinancialSettings();
  const updateMutation = useUpdateFinancialSettings();
  const syncMutation = useFinancialSync();

  const current = settingsData?.data;

  const [enabled, setEnabled] = useState(current?.enabled ?? false);
  const [syncIntervalMinutes, setSyncIntervalMinutes] = useState(
    current?.syncIntervalMinutes ?? 60
  );
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [mcpServerCommand, setMcpServerCommand] = useState(current?.mcpServerCommand ?? '');

  // Detect if using the simple key mode or advanced command mode
  const isSimpleMode = !mcpServerCommand || mcpServerCommand.includes('--api-key=');

  function extractKeyFromCommand(cmd: string): string {
    const match = cmd.match(/--api-key=(\S+)/);
    return match ? match[1] : '';
  }

  function buildCommand(key: string): string {
    return `npx -y @stripe/mcp --tools=all --api-key=${key}`;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    let finalCommand = mcpServerCommand;
    if (isSimpleMode && stripeSecretKey) {
      finalCommand = buildCommand(stripeSecretKey);
    } else if (isSimpleMode && !stripeSecretKey && current?.mcpServerCommand) {
      finalCommand = current.mcpServerCommand;
    }

    if (enabled && !finalCommand) {
      toast.error('Informe a Secret Key da Stripe');
      return;
    }

    updateMutation.mutate(
      { enabled, syncIntervalMinutes, mcpServerCommand: finalCommand },
      {
        onSuccess: () => {
          toast.success('Configuracao Stripe salva');
          if (enabled && finalCommand) {
            syncMutation.mutate(undefined, {
              onSuccess: () => toast.success('Primeiro sync iniciado!'),
            });
          }
          onClose();
        },
        onError: () => toast.error('Erro ao salvar configuracao'),
      }
    );
  }

  const currentKey = current?.mcpServerCommand
    ? extractKeyFromCommand(current.mcpServerCommand)
    : '';
  const maskedKey = currentKey ? `${currentKey.slice(0, 7)}...${currentKey.slice(-4)}` : '';

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-secondary-200">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-secondary-900">Configurar Stripe</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary-100">
            <X className="w-5 h-5 text-secondary-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-secondary-600">Ativo</label>
            <button
              type="button"
              onClick={() => setEnabled(!enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-primary-600' : 'bg-secondary-300'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-600 mb-1">
              Stripe Secret Key
            </label>
            <input
              type="password"
              value={stripeSecretKey}
              onChange={(e) => setStripeSecretKey(e.target.value)}
              placeholder={maskedKey || 'sk_live_... ou sk_test_...'}
              className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            {maskedKey && !stripeSecretKey && (
              <p className="text-xs text-secondary-400 mt-1">
                Chave atual: {maskedKey} — deixe vazio para manter
              </p>
            )}
            <p className="text-xs text-secondary-400 mt-1">
              Encontre em Stripe Dashboard → Developers → API keys
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-600 mb-1">
              Intervalo de Sync (minutos)
            </label>
            <input
              type="number"
              min={5}
              max={1440}
              value={syncIntervalMinutes}
              onChange={(e) => setSyncIntervalMinutes(Number(e.target.value))}
              className="w-32 rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            <p className="text-xs text-secondary-400 mt-1">
              De 5 min a 1440 min (24h). Recomendado: 60 min
            </p>
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-secondary-500 hover:text-secondary-700">
              Configuracao avancada
            </summary>
            <div className="mt-3">
              <label className="block text-sm font-medium text-secondary-600 mb-1">
                Comando MCP Server
              </label>
              <input
                type="text"
                value={mcpServerCommand}
                onChange={(e) => setMcpServerCommand(e.target.value)}
                placeholder="npx -y @stripe/mcp --tools=all --api-key=sk_..."
                className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
              <p className="text-xs text-secondary-400 mt-1">
                Override completo do comando. Ao preencher, a Secret Key acima e ignorada.
              </p>
            </div>
          </details>

          <div className="border-t border-secondary-200 pt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-secondary-600 bg-secondary-100 rounded-lg hover:bg-secondary-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {updateMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
